import React from "react";
import {
  act,
  fireEvent,
  render,
  screen,
  waitFor,
} from "@testing-library/react";
import { ToolComposingIndicator } from "../components/ToolComposingIndicator";
import { useToolComposingFeedback } from "../hooks/useToolComposingFeedback";
import {
  buildIdaChatStreamConfig,
  subscribeToConversationSseFrames,
} from "../utils/idaChatStreamConfig";

const API_BASE_URL = "https://example.test/api/workspace/ida";
const encoder = new TextEncoder();

function createResponse(body: ReadableStream<Uint8Array>): Response {
  return {
    ok: true,
    status: 200,
    headers: new Headers(),
    body,
  } as unknown as Response;
}

describe("typed POST tool-composing feedback", () => {
  const originalFetch = global.fetch;
  const originalWindowFetch = window.fetch;

  afterEach(() => {
    global.fetch = originalFetch;
    window.fetch = originalWindowFetch;
  });

  it("shows the composing label from the typed POST stream until the call starts", async () => {
    const observedFrames: unknown[] = [];
    const unsubscribe = subscribeToConversationSseFrames((frame) =>
      observedFrames.push(frame),
    );
    let finishPostStream!: () => void;
    let postCloneCount = 0;
    const fetchMock = jest.fn(
      async (input: RequestInfo | URL, init?: RequestInit) => {
        if (init?.method === "GET") {
          return createResponse(new ReadableStream<Uint8Array>({ start() {} }));
        }

        const finishers: Array<() => void> = [];
        const makePostResponse = (): Response => {
          const body = new ReadableStream<Uint8Array>({
            start(controller) {
              controller.enqueue(
                encoder.encode(
                  'data: {"type":"tool_composing","call_id":"call-1","tool_name":"generate_document","label":"Writing document"}\n\n',
                ),
              );
              finishers.push(() => {
                controller.enqueue(
                  encoder.encode(
                    'data: {"type":"tool_calling","tool_call_id":"call-1"}\n\n' +
                      'data: {"type":"done"}\n\n',
                  ),
                );
                controller.close();
              });
            },
          });
          return {
            ...createResponse(body),
            clone: () => {
              postCloneCount += 1;
              return makePostResponse();
            },
          } as Response;
        };
        const response = makePostResponse();
        finishPostStream = () => finishers.forEach((finish) => finish());
        return response;
      },
    ) as typeof fetch;
    global.fetch = fetchMock;
    window.fetch = fetchMock;
    const streamConfig = buildIdaChatStreamConfig(API_BASE_URL);
    // Browser builds share window.fetch and globalThis.fetch. Align the two in
    // jsdom before ChatProvider captures its stream fetch implementation.
    global.fetch = window.fetch;
    const { ChatProvider, useChat, useChatStore } =
      await import("@metalmind-ab/mmagent-chat-ui");
    useChatStore.getState().setConversationId("42");

    function TypedSendHarness() {
      const { sendMessage } = useChat();
      const conversationId = useChatStore((state) => state.conversationId);
      const { label } = useToolComposingFeedback(conversationId);

      return (
        <>
          <button onClick={() => void sendMessage("Write a long document")}>
            Send
          </button>
          {label ? <ToolComposingIndicator label={label} /> : null}
        </>
      );
    }

    render(
      <ChatProvider
        config={{
          apiBaseUrl: API_BASE_URL,
          ...streamConfig,
        }}
      >
        <TypedSendHarness />
      </ChatProvider>,
    );

    fireEvent.click(screen.getByRole("button", { name: "Send" }));
    await waitFor(() =>
      expect(fetchMock).toHaveBeenCalledWith(
        `${API_BASE_URL}/chat/conversations/42/messages/stream`,
        expect.objectContaining({ method: "POST" }),
      ),
    );
    await waitFor(() => expect(postCloneCount).toBe(1));
    await waitFor(() =>
      expect(observedFrames).toContainEqual(
        expect.objectContaining({
          type: "tool_composing",
          conversation_id: "42",
          call_id: "call-1",
          label: "Writing document",
        }),
      ),
    );
    expect(
      await screen.findByText("Preparing: Writing document…"),
    ).toBeTruthy();

    act(() => {
      finishPostStream();
    });
    await waitFor(() =>
      expect(screen.queryByText("Preparing: Writing document…")).toBeNull(),
    );
    unsubscribe();
    act(() => {
      useChatStore.getState().clearMessages();
      useChatStore.getState().setConversationId(null);
      useChatStore.getState().setLoading(false);
    });
  });
});
